import React from "react";

import type { YaasPositionCardData } from "../../types/yaas";

import { YaasPositionBalance } from "./YaasPositionBalance";

interface YaasPositionCardProps {
    position: YaasPositionCardData;
    formattedTotal?: string;
}

/**
 * The Account Balance card: the position's balance, and nothing else.
 *
 * Everything state-specific it used to own now sits outside it, in one
 * slot each, so the column reads the same order in every state —
 * balance, then destination rows, then one action. The status is the
 * badge beside the page heading, the below-minimum reason is that
 * badge's tooltip, and the action is `PositionsActionRow`.
 *
 * The preview / ghost mode and the `traceBorder*` entrance
 * animation both moved to `YaasPreviewBalanceCard`, which is what
 * `YaasEarnProjectionCTA` actually renders. This card's copies were
 * left behind by that refactor and are gone.
 *
 * Mechanism-only / SDK-free. Hoisted into `@solomon-apps/shared`
 * (widgets/yaas) so the sim-mode `PositionsPanel` renders externally.
 * apps/main re-exports it from its old path via a shim.
 */
export function YaasPositionCard({
    position,
    formattedTotal
}: YaasPositionCardProps): React.ReactElement {
    return (
        // Flat-on-page exploration: dropped the border-2 +
        // accent-top stripe and the p-6 / md:p-8 padding so the
        // card chrome doesn't compete with the right-column hero
        // surfaces around it. Keep `borderless` honoring its
        // existing no-border behavior. Revert this branch (commit
        // 58aeb863) if the chrome reads better than the flat
        // version.
        <div className="flex flex-col gap-8 relative">
            {/* Balance group — label + status pill row moved out of
                the card. The page-level "Positions" heading now
                owns the status pill via its `trailing` slot, and
                the "Total" unit label sits below the amount as
                part of the YaasPositionBalance primitive. */}
            <div className="flex flex-col gap-2">
                <YaasPositionBalance
                    formatted={
                        formattedTotal ?? position.position.formattedUSDvValue
                    }
                />

                {/* Paused explainer + Buy USDv affordance — deferred.
                    Stripped down to render the standard pending-style
                    card layout (header + balance + distribution group
                    + Rewards Destination + Manage Position) so the
                    paused state reads as a clean variant of pending,
                    differentiated only by the "Paused" pill, the
                    smaller balance, and the empty Next Distribution
                    "—". The "Add USDv to resume" copy + Buy USDv link
                    can come back as a layered affordance once the base
                    paused layout is dialed in. */}
            </div>
        </div>
    );
}
